// src/ui/screens/SubscribersSubscribedScreen.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { db } from '../../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  documentId,
} from 'firebase/firestore';
import { UserItem } from '../../components/UserItem';

const COLORS = {
  bg: '#08090B',
  card: '#23262C',
  orange: '#FF8C08',
};

const PAGE_SIZE = 20;
// Firestore's whereIn has a limit of 10 items per query
const BATCH_SIZE = 10;

const mapUserDoc = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    displayName: data.displayName || data.name || 'Unknown',
    username: data.username || '',
    profileImage: data.profileImage || data.photoURL || '',
    verified: data.isVerified === true || data.verified === true,
    isVerifiedSeller: data.isVerifiedSeller === true || data.isVerifiedSeller === 'true',
    verification: data.verification,
    categoryType: data.categoryType || '',
    subscribersCount: (data.subscribers || []).length,
    subscribers: data.subscribers || [],
  };
};

export const SubscribersSubscribedScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { uid } = useParams();
  const [displayName, setDisplayName] = useState(location.state?.displayName || '');
  const [activeTab, setActiveTab] = useState(location.state?.initialTabIndex || 0);

  // Subscribers state
  const [subscribers, setSubscribers] = useState([]);
  const [loadingSubscribers, setLoadingSubscribers] = useState(true);
  const [loadingMoreSubscribers, setLoadingMoreSubscribers] = useState(false);
  const [hasMoreSubscribers, setHasMoreSubscribers] = useState(true);
  const subscribersOffsetRef = useRef(0);
  const subscriberIdsRef = useRef([]);

  // Subscribed state
  const [subscribed, setSubscribed] = useState([]);
  const [loadingSubscribed, setLoadingSubscribed] = useState(true);
  const [loadingMoreSubscribed, setLoadingMoreSubscribed] = useState(false);
  const [hasMoreSubscribed, setHasMoreSubscribed] = useState(true);
  const subscribedOffsetRef = useRef(0);
  const subscribedIdsRef = useRef([]);

  // Scroll refs
  const subscribersScrollRef = useRef(null);
  const subscribedScrollRef = useRef(null);

  // ── Fetch displayName if we arrived without state ────────────────────────

  useEffect(() => {
    if (displayName || !uid) return;
    getDoc(doc(db, 'users', uid))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setDisplayName(data.displayName || data.name || 'User');
        }
      })
      .catch((e) => console.error('Load displayName error:', e));
  }, [uid, displayName]);

  // ── Helper to fetch user documents in batches ────────────────────────────

  const fetchUsersInBatches = async (userIds, offset, limit) => {
    if (userIds.length === 0) return { users: [], hasMore: false };

    const start = offset;
    const end = Math.min(start + PAGE_SIZE, userIds.length);
    const batchIds = userIds.slice(start, end);
    
    if (batchIds.length === 0) {
      return { users: [], hasMore: false };
    }

    const users = [];
    
    // Firestore whereIn only supports up to 10 items per query
    for (let i = 0; i < batchIds.length; i += BATCH_SIZE) {
      const batch = batchIds.slice(i, i + BATCH_SIZE);
      const usersSnapshot = await getDocs(
        query(collection(db, 'users'), where(documentId(), 'in', batch))
      );
      
      const batchUsers = usersSnapshot.docs.map(mapUserDoc);
      users.push(...batchUsers);
    }

    // Sort to maintain original order
    users.sort((a, b) => {
      const indexA = batchIds.indexOf(a.id);
      const indexB = batchIds.indexOf(b.id);
      return indexA - indexB;
    });

    const hasMore = end < userIds.length;

    return { users, hasMore };
  };

  // ── Load Subscribers ──────────────────────────────────────────────────────

  const loadSubscribers = useCallback(async (loadMore = false) => {
    if (!uid) return;

    try {
      if (!loadMore) {
        setLoadingSubscribers(true);
        setSubscribers([]);
        subscribersOffsetRef.current = 0;
        setHasMoreSubscribers(true);
        
        // Get subscriber IDs from the user document
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (!userDoc.exists()) {
          setLoadingSubscribers(false);
          setHasMoreSubscribers(false);
          return;
        }
        
        const data = userDoc.data();
        subscriberIdsRef.current = data.subscribers || [];
        
        if (subscriberIdsRef.current.length === 0) {
          setLoadingSubscribers(false);
          setHasMoreSubscribers(false);
          return;
        }
      } else {
        if (loadingMoreSubscribers || !hasMoreSubscribers) return;
        setLoadingMoreSubscribers(true);
      }

      const offset = loadMore ? subscribersOffsetRef.current : 0;
      const { users, hasMore } = await fetchUsersInBatches(
        subscriberIdsRef.current,
        offset,
        PAGE_SIZE
      );

      if (loadMore) {
        setSubscribers(prev => [...prev, ...users]);
      } else {
        setSubscribers(users);
      }

      subscribersOffsetRef.current = offset + users.length;
      setHasMoreSubscribers(hasMore);
      
    } catch (error) {
      console.error('Load subscribers error:', error);
    } finally {
      if (loadMore) {
        setLoadingMoreSubscribers(false);
      } else {
        setLoadingSubscribers(false);
      }
    }
  }, [uid, loadingMoreSubscribers, hasMoreSubscribers]);

  // ── Load Subscribed ──────────────────────────────────────────────────────

  const loadSubscribed = useCallback(async (loadMore = false) => {
    if (!uid) return;

    try {
      if (!loadMore) {
        setLoadingSubscribed(true);
        setSubscribed([]);
        subscribedOffsetRef.current = 0;
        setHasMoreSubscribed(true);
        
        // Get subscribed IDs from the user document
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (!userDoc.exists()) {
          setLoadingSubscribed(false);
          setHasMoreSubscribed(false);
          return;
        }
        
        const data = userDoc.data();
        subscribedIdsRef.current = data.subscribed || [];
        
        if (subscribedIdsRef.current.length === 0) {
          setLoadingSubscribed(false);
          setHasMoreSubscribed(false);
          return;
        }
      } else {
        if (loadingMoreSubscribed || !hasMoreSubscribed) return;
        setLoadingMoreSubscribed(true);
      }

      const offset = loadMore ? subscribedOffsetRef.current : 0;
      const { users, hasMore } = await fetchUsersInBatches(
        subscribedIdsRef.current,
        offset,
        PAGE_SIZE
      );

      if (loadMore) {
        setSubscribed(prev => [...prev, ...users]);
      } else {
        setSubscribed(users);
      }

      subscribedOffsetRef.current = offset + users.length;
      setHasMoreSubscribed(hasMore);
      
    } catch (error) {
      console.error('Load subscribed error:', error);
    } finally {
      if (loadMore) {
        setLoadingMoreSubscribed(false);
      } else {
        setLoadingSubscribed(false);
      }
    }
  }, [uid, loadingMoreSubscribed, hasMoreSubscribed]);

  // ── Scroll handlers ──────────────────────────────────────────────────────

  const handleSubscribersScroll = useCallback(() => {
    if (!subscribersScrollRef.current) return;
    const el = subscribersScrollRef.current;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 300) {
      if (!loadingMoreSubscribers && hasMoreSubscribers) {
        loadSubscribers(true);
      }
    }
  }, [loadSubscribers, loadingMoreSubscribers, hasMoreSubscribers]);

  const handleSubscribedScroll = useCallback(() => {
    if (!subscribedScrollRef.current) return;
    const el = subscribedScrollRef.current;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 300) {
      if (!loadingMoreSubscribed && hasMoreSubscribed) {
        loadSubscribed(true);
      }
    }
  }, [loadSubscribed, loadingMoreSubscribed, hasMoreSubscribed]);

  // ── Initial loads ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!uid) return;
    loadSubscribers(false);
    loadSubscribed(false);
  }, [uid]);

  // ── Render helpers ──────────────────────────────────────────────────────

  const renderEmptyState = (label) => (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <svg className="w-12 h-12 text-white/12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
      <p className="text-white/35 text-sm font-medium">{label}</p>
    </div>
  );

  const renderUserList = (users, loading, loadingMore, emptyLabel, scrollRef, onScroll) => {
    if (loading && users.length === 0) {
      return (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (users.length === 0) {
      return renderEmptyState(emptyLabel);
    }

    return (
      <div ref={scrollRef} onScroll={onScroll} className="h-full overflow-y-auto pb-20">
        <div className="divide-y divide-white/5">
          {users.map((u) => (
            <UserItem key={u.id} user={u} />
          ))}
        </div>
        {loadingMore && (
          <div className="flex justify-center py-4">
            <div className="w-6 h-6 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    );
  };

  // ── Main render ──────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* App Bar */}
      <div className="sticky top-0 z-10 bg-filatom-bg border-b border-white/5">
        <div className="flex items-center h-14 px-4">
          <button
            onClick={() => navigate(-1)}
            className="w-11 h-11 rounded-full flex items-center justify-center hover:bg-white/5 transition-colors"
          >
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h1 className="flex-1 text-center text-white text-lg font-bold truncate px-4">
            {displayName || '...'}
          </h1>
          <div className="w-11" />
        </div>

        {/* Tab Bar */}
        <div className="flex px-4">
          {['Subscribers', 'Subscribed'].map((tab, index) => (
            <button
              key={tab}
              onClick={() => setActiveTab(index)}
              className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
                activeTab === index ? 'text-white' : 'text-white/40'
              }`}
            >
              {tab}
              {activeTab === index && (
                <div
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full"
                  style={{ backgroundColor: COLORS.orange }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="h-[calc(100vh-112px)]">
        {activeTab === 0 && renderUserList(
          subscribers,
          loadingSubscribers,
          loadingMoreSubscribers,
          'No subscribers yet',
          subscribersScrollRef,
          handleSubscribersScroll
        )}
        {activeTab === 1 && renderUserList(
          subscribed,
          loadingSubscribed,
          loadingMoreSubscribed,
          'Not subscribed to anyone yet',
          subscribedScrollRef,
          handleSubscribedScroll
        )}
      </div>
    </div>
  );
};

export default SubscribersSubscribedScreen;